import React, { useState } from "react";
import { api, unwrap, errMsg } from "@/lib/api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { formatDate } from "@/lib/utils";
import {
  GraduationCap,
  Plus,
  LockKeyhole,
  FileText,
  Award,
} from "lucide-react";
import { useI18n } from "@/i18n";
import { useLookupValues } from "@/lib/platformConfig";
import { DatePicker } from "@/components/ui/calendar";
import { useAuth } from "@/lib/auth";
import { levelDisplayName, levelWord, levelSystemOf } from "@/lib/levelLabels";

export default function Exams() {
  const { t, language } = useI18n();
  const { user } = useAuth();
  const mode = levelSystemOf(user);
  const [open, setOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [addScheduleOpen, setAddScheduleOpen] = useState(false);
  const {
    data: exams,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["exams"],
    queryFn: async () => unwrap(await api.get("/exams")),
  });
  const { data: grades } = useQuery({
    queryKey: ["grades"],
    queryFn: async () => unwrap(await api.get("/grades")),
  });
  const { data: subjects } = useQuery({
    queryKey: ["subjects"],
    queryFn: async () => unwrap(await api.get("/subjects")),
  });
  const { data: sections } = useQuery({
    queryKey: ["sections"],
    queryFn: async () => unwrap(await api.get("/sections")),
  });
  const { data: years } = useQuery({
    queryKey: ["ay"],
    queryFn: async () => unwrap(await api.get("/academic-years")),
  });
  const qc = useQueryClient();

  const createMut = useMutation({
    mutationFn: async (form) => unwrap(await api.post("/exams", form)),
    onSuccess: () => {
      toast.success("Exam created");
      refetch();
      setOpen(false);
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const { data: detail, refetch: refetchDetail } = useQuery({
    queryKey: ["exam", detailId],
    queryFn: async () => unwrap(await api.get(`/exams/${detailId}`)),
    enabled: !!detailId,
  });

  const addScheduleMut = useMutation({
    mutationFn: async (payload) =>
      unwrap(
        await api.post(`/exams/${detailId}/schedules`, {
          ...payload,
          exam_id: detailId,
        }),
      ),
    onSuccess: () => {
      toast.success("Schedule added");
      refetchDetail();
      setAddScheduleOpen(false);
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const lockMut = useMutation({
    mutationFn: async (scheduleId) =>
      unwrap(await api.post(`/exam-schedules/${scheduleId}/lock`)),
    onSuccess: () => {
      toast.success("Marks locked");
      refetchDetail();
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const generateReportsMut = useMutation({
    mutationFn: async () =>
      unwrap(await api.post("/report-cards/generate", { exam_id: detailId })),
    onSuccess: (d) => {
      toast.success(`${d.generated + d.updated} report cards ready`);
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const currentYear =
    (years || []).find((y) => y.is_current) || (years || [])[0];

  return (
    <div className="p-6 lg:p-8" data-testid="exams-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-bold text-[var(--text-primary)]">
            {t("management.examsTitle")}
          </h1>
          <p className="text-sm text-[var(--text-muted)] mt-1">
            Term exams, schedules and marks
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button
              className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white"
              data-testid="new-exam-btn"
            >
              <Plus className="h-4 w-4 mr-1.5" /> New exam
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-display">New exam</DialogTitle>
            </DialogHeader>
            <NewExamForm
              grades={grades || []}
              years={years || []}
              currentYear={currentYear}
              onSubmit={(f) => createMut.mutate(f)}
              loading={createMut.isPending}
            />
          </DialogContent>
        </Dialog>
      </div>

      {isLoading ? (
        <Skeleton className="h-64" />
      ) : (
        <div className="grid md:grid-cols-2 gap-3 stagger">
          {(exams || []).map((e) => {
            const g = (grades || []).find((g) => g.id === e.grade_id);
            return (
              <Card
                key={e.id}
                className="border-[var(--subtle)] lift-card cursor-pointer"
                onClick={() => setDetailId(e.id)}
                data-testid={`exam-${e.id}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-[var(--text-muted)] font-semibold">
                        {levelDisplayName(g, language, grades)}
                      </div>
                      <div className="font-semibold text-[var(--text-primary)] mt-1">
                        {e.name}
                      </div>
                      <div className="text-xs text-[var(--text-muted)] mt-1">
                        {formatDate(e.start_date)} → {formatDate(e.end_date)}
                      </div>
                    </div>
                    <Badge className="bg-[var(--brand)] text-white border-0 capitalize">
                      {e.status?.replace("_", " ")}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={!!detailId} onOpenChange={(v) => !v && setDetailId(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">{detail?.name}</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Button
                  size="sm"
                  onClick={() => setAddScheduleOpen(true)}
                  className="bg-[var(--brand)] hover:bg-[var(--brand-hover)] text-white"
                  data-testid="add-schedule-btn"
                >
                  <Plus className="h-4 w-4 mr-1.5" /> Add subject schedule
                </Button>
                <Button
                  size="sm"
                  onClick={() => generateReportsMut.mutate()}
                  disabled={generateReportsMut.isPending}
                  className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white"
                  data-testid="generate-report-cards"
                >
                  <Award className="h-4 w-4 mr-1.5" /> Generate report cards
                </Button>
              </div>
              <div className="border rounded overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-[var(--bg)]">
                    <tr>
                      <th className="text-left px-3 py-2 text-[11px] uppercase tracking-widest text-[var(--text-muted)] font-semibold">
                        Subject
                      </th>
                      <th className="text-left px-3 py-2 text-[11px] uppercase tracking-widest text-[var(--text-muted)] font-semibold">
                        Date
                      </th>
                      <th className="text-right px-3 py-2 text-[11px] uppercase tracking-widest text-[var(--text-muted)] font-semibold">
                        Max
                      </th>
                      <th className="text-left px-3 py-2 text-[11px] uppercase tracking-widest text-[var(--text-muted)] font-semibold">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {(detail.schedules || []).map((s) => (
                      <tr
                        key={s.id}
                        className="border-t border-[var(--subtle)]"
                      >
                        <td className="px-3 py-2 font-medium">
                          {s.subject?.name}
                        </td>
                        <td className="px-3 py-2 font-mono-num text-xs">
                          {formatDate(s.exam_date)} · {s.start_time}
                        </td>
                        <td className="px-3 py-2 text-right font-mono-num">
                          {s.max_marks}
                        </td>
                        <td className="px-3 py-2">
                          {s.locked ? (
                            <Badge className="bg-[var(--warning-bg)] text-[var(--warning)] border-0">
                              <LockKeyhole className="h-3 w-3 mr-1" /> Locked
                            </Badge>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7"
                              onClick={() => lockMut.mutate(s.id)}
                              data-testid={`lock-${s.id}`}
                            >
                              Lock
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                    {(detail.schedules || []).length === 0 && (
                      <tr>
                        <td
                          colSpan={4}
                          className="py-6 text-center text-[var(--text-muted)]"
                        >
                          No subjects scheduled yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={addScheduleOpen} onOpenChange={setAddScheduleOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display">
              Add subject to exam
            </DialogTitle>
          </DialogHeader>
          <NewScheduleForm
            subjects={subjects || []}
            sections={(sections || []).filter(
              (s) => s.grade_id === detail?.grade_id,
            )}
            onSubmit={(f) => addScheduleMut.mutate(f)}
            loading={addScheduleMut.isPending}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function NewExamForm({ grades, years, currentYear, onSubmit, loading }) {
  const examTypesLookup = useLookupValues("exam_types");
  const examTypeOptions = (examTypesLookup.data?.values || []).map((row) => ({ value: row.value, label: row.label }));
  const typeOptions = examTypeOptions;
  const [form, setForm] = useState({
    name: "Term 2 Final",
    academic_year_id: currentYear?.id || "",
    grade_id: "",
    start_date: new Date().toISOString().slice(0, 10),
    end_date: new Date().toISOString().slice(0, 10),
    exam_type: "term",
  });
  React.useEffect(() => {
    if (currentYear)
      setForm((f) =>
        f.academic_year_id ? f : { ...f, academic_year_id: currentYear.id },
      );
  }, [currentYear]);
  React.useEffect(() => {
    if (!typeOptions.length) return;
    if (typeOptions.some((option) => option.value === form.exam_type)) return;
    setForm((prev) => ({ ...prev, exam_type: typeOptions[0].value }));
  }, [form.exam_type, typeOptions]);
  return (
    <div className="space-y-3">
      <div>
        <Label className="text-xs uppercase">Name</Label>
        <Input
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          className="mt-1"
          data-testid="exam-name"
        />
      </div>
      <div>
        <Label className="text-xs uppercase">{levelWord(mode, language)}</Label>
        <Select
          value={form.grade_id}
          onValueChange={(v) => setForm({ ...form, grade_id: v })}
        >
          <SelectTrigger className="mt-1" data-testid="exam-grade">
            <SelectValue placeholder={levelWord(mode, language)} />
          </SelectTrigger>
          <SelectContent>
            {grades.map((g) => (
              <SelectItem key={g.id} value={g.id}>
                {levelDisplayName(g, language, grades)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs uppercase">Start</Label>
          <DatePicker
            value={form.start_date}
            onChange={(date) => setForm({ ...form, start_date: date })}
          />
        </div>
        <div>
          <Label className="text-xs uppercase">End</Label>
          <DatePicker
            value={form.end_date}
            onChange={(date) => setForm({ ...form, end_date: date })}
          />
        </div>
      </div>
      <div>
        <Label className="text-xs uppercase">Type</Label>
        <Select
          value={form.exam_type}
          onValueChange={(v) => setForm({ ...form, exam_type: v })}
        >
          <SelectTrigger className="mt-1">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {typeOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <DialogFooter>
        <Button
          onClick={() => onSubmit(form)}
          disabled={!form.grade_id || !form.name || loading}
          className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white"
          data-testid="submit-exam"
        >
          {loading ? "Creating..." : "Create exam"}
        </Button>
      </DialogFooter>
    </div>
  );
}

function NewScheduleForm({ subjects, sections, onSubmit, loading }) {
  const [form, setForm] = useState({
    subject_id: "",
    section_id: sections[0]?.id || "",
    exam_date: new Date().toISOString().slice(0, 10),
    start_time: "09:00",
    duration_minutes: 90,
    max_marks: 100,
  });
  React.useEffect(() => {
    if (sections[0])
      setForm((f) => (f.section_id ? f : { ...f, section_id: sections[0].id }));
  }, [sections]);
  return (
    <div className="space-y-3">
      <div>
        <Label className="text-xs uppercase">Subject</Label>
        <Select
          value={form.subject_id}
          onValueChange={(v) => setForm({ ...form, subject_id: v })}
        >
          <SelectTrigger className="mt-1" data-testid="sched-subject">
            <SelectValue placeholder="Subject" />
          </SelectTrigger>
          <SelectContent>
            {subjects.map((s) => (
              <SelectItem key={s.id} value={s.id}>
                {s.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs uppercase">Section</Label>
        <Select
          value={form.section_id}
          onValueChange={(v) => setForm({ ...form, section_id: v })}
        >
          <SelectTrigger className="mt-1">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {sections.map((s) => (
              <SelectItem key={s.id} value={s.id}>
                {s.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <Label className="text-xs uppercase">Date</Label>
          <DatePicker
            value={form.exam_date}
            onChange={(date) => setForm({ ...form, exam_date: date })}
          />
        </div>
        <div>
          <Label className="text-xs uppercase">Start</Label>
          <Input
            type="time"
            value={form.start_time}
            onChange={(e) => setForm({ ...form, start_time: e.target.value })}
            className="mt-1"
          />
        </div>
        <div>
          <Label className="text-xs uppercase">Max</Label>
          <Input
            type="number"
            value={form.max_marks}
            onChange={(e) =>
              setForm({ ...form, max_marks: Number(e.target.value) })
            }
            className="mt-1 font-mono-num"
          />
        </div>
      </div>
      <DialogFooter>
        <Button
          onClick={() => onSubmit(form)}
          disabled={!form.subject_id || !form.section_id || loading}
          className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white"
          data-testid="submit-schedule"
        >
          {loading ? "Adding..." : "Add subject"}
        </Button>
      </DialogFooter>
    </div>
  );
}
